'use client';

import * as React from 'react';

import { Button } from '@/hen-ui/atomics/Button';
import { Heading } from '@/hen-ui/atomics/Heading';
import type { IconName } from '@/hen-ui/atomics/Icon';
import { Image } from '@/hen-ui/atomics/Image';
import { Text } from '@/hen-ui/atomics/Text';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  Carousel,
  CarouselItem,
  type CarouselApi,
} from '@/hen-ui/composites/Carousel';
import type {
  CarouselItemBasisBreakpointToken,
  CarouselItemBasisToken,
} from '@/hen-ui/composites/Carousel/carousel.variants';
import { Flex } from '@/hen-ui/composites/Flex';
import { Grid } from '@/hen-ui/composites/Grid';
import { Section } from '@/hen-ui/composites/Section';
import { useMatchesMedia } from '@/hen-ui/hooks/useIsDesktop';
import { cn } from '@/hen-ui/utils';
import { useReducedMotion } from '@/hen-ui/hooks/useReducedMotion';
import { useEnclosingModal } from '@/hen-ui/composites/Modal/ModalHost';
import { DIALOG_OPENED_EVENT } from '@/hen-ui/shadcn/dialog';

export type MediaItemStatus = 'ready' | 'loading' | 'error';
export type MediaCountPlacement = 'start' | 'end';
export type MediaImageFit = 'cover' | 'contain';

export interface MediaItem {
  id: string;
  src: string;
  alt: string;
  status?: MediaItemStatus;
}

export interface MediaGroup {
  id: string;
  items: MediaItem[];
}

export interface MediaGalleryProps {
  items?: MediaItem[];
  /**
   * Library collections, shown in order as one list. Takes the place of
   * `items` when it has photos, so this gallery and a library can read the
   * same data and number photos the same way.
   */
  groups?: MediaGroup[];
  selectedIndex?: number;
  defaultSelectedIndex?: number;
  /**
   * Position sync. Fires whenever the current item changes, including from a
   * swipe or arrow key, so it is the wrong hook for opening a dialog.
   */
  onSelectedIndexChange?: (index: number, item: MediaItem) => void;
  /**
   * Deliberate choice of an item: click, Enter, or Space on a tile. Fires even
   * when the item was already selected, and never from browsing. Bind it to
   * an Open modal action with `index: '{{event}}'` to open a popup on the
   * chosen photo.
   */
  onItemActivate?: (index: number, item: MediaItem) => void;
  lastVisibleOverlay?: React.ReactNode;
  overlayActions?: React.ReactNode;
  showAsCarousel?: boolean;
  /**
   * Lets the mobile carousel extend through its host page's horizontal gutter
   * to the viewport edges. The gallery keeps its own edge inset and returns to
   * its normal width when the entry gallery switches to the grid at 800px.
   * @default true
   */
  bleedSectionPadding?: boolean;
  /**
   * Chrome above the slides when `showAsCarousel` is on. Rendered in the same
   * column as the viewport (between beside arrows) so it can share the photo's
   * left edge from `md` up.
   */
  carouselHeader?: React.ReactNode;
  /**
   * How wide each swipe slide is. Leave unset for the default entry layout
   * (full on phones, two-up from tablet) or one-up viewer. Use `peek` for a
   * thin sliver of the previous and next photos around the current one. On the
   * entry gallery that peek stays until the grid takes over at 800px, unless
   * `slideBasis-md` sets a tablet width. On the one-up viewer, tablet and up
   * return to one full photo unless a breakpoint override is set.
   */
  slideBasis?: CarouselItemBasisToken;
  /** From tablet upward, override how wide each swipe slide is. */
  'slideBasis-md'?: CarouselItemBasisBreakpointToken;
  /** From desktop upward, override how wide each swipe slide is. */
  'slideBasis-lg'?: CarouselItemBasisBreakpointToken;
  imageBorder?: boolean;
  imageFit?: MediaImageFit;
  showCount?: boolean;
  countPlacement?: MediaCountPlacement;
  countIcon?: IconName;
  label?: string;
  loadingLabel?: string;
  errorLabel?: string;
}

type MediaGallerySlideBasisProps = Pick<
  MediaGalleryProps,
  'slideBasis' | 'slideBasis-md' | 'slideBasis-lg'
>;

const MEDIA_GALLERY_MD_QUERY = '(min-width: 768px)';
const MEDIA_GALLERY_LG_QUERY = '(min-width: 1024px)';

/**
 * Configured width per breakpoint. Tablet does not inherit the phone width:
 * unset tablet keeps the default layout, and desktop inherits tablet.
 */
const resolveMediaGallerySlideTiers = ({
  slideBasis,
  'slideBasis-md': slideBasisMd,
  'slideBasis-lg': slideBasisLg,
}: MediaGallerySlideBasisProps) => ({
  base: slideBasis,
  md: slideBasisMd,
  lg: slideBasisLg ?? slideBasisMd,
});

const isDefaultSlideWidth = (
  width: CarouselItemBasisToken | undefined,
): boolean => width === undefined || width === 'full';

// Mirrored by the Tailwind classes below (used for peek align math).
const MEDIA_GALLERY_EDGE_PADDING_PX = 16;
const MEDIA_GALLERY_PEEK_GAP_PX = 14;

// Literal strings -- Tailwind's JIT scanner can't resolve a computed class.
const MEDIA_GALLERY_PEEK_SLIDE_GUTTER_CLASS = 'pl-[0.875rem]';
const MEDIA_GALLERY_PEEK_TRACK_RELATIVE_CLASS =
  'relative [&>div]:relative [&>div]:-ml-[0.875rem]';
const MEDIA_GALLERY_PEEK_EDGE_SHIFT_CLASS = '[&>div]:translate-x-[16px]';
const MEDIA_GALLERY_PEEK_OVERLAY_LEFT_CLASS = 'left-[calc(16px+12px)]';
const MEDIA_GALLERY_PEEK_OVERLAY_BOTTOM_CLASS = 'bottom-[16px]';

/**
 * Embla `align` for peek: first and last photos keep the carousel's 1rem edge
 * padding, while middle slides stay centered for equal neighbor peeks. Each
 * slide's 14px gutter is cancelled by the track's negative margin, so a photo's
 * left edge sits at Embla's align position and centering the slide centers the
 * photo.
 *
 * Embla measures slides with `offsetLeft`, relative to the nearest offset
 * parent. Peek makes the track its own offset parent (`relative`), so the
 * track's -14px margin reads as a 14px start gap on every slide. Adding it back
 * keeps the measurement identical before and after Embla's own transform lands.
 */
export const createMediaGalleryPeekAlign = (
  slideCount: number,
): ((viewSize: number, snapSize: number, index: number) => number) => {
  const lastIndex = Math.max(slideCount - 1, 0);
  return (viewSize, snapSize, index) => {
    if (slideCount <= 1) {
      return (viewSize - snapSize) / 2 + MEDIA_GALLERY_PEEK_GAP_PX;
    }
    if (index === 0) {
      return MEDIA_GALLERY_EDGE_PADDING_PX + MEDIA_GALLERY_PEEK_GAP_PX;
    }
    if (index === lastIndex) {
      return (
        Math.max(0, viewSize - snapSize - MEDIA_GALLERY_EDGE_PADDING_PX) +
        MEDIA_GALLERY_PEEK_GAP_PX
      );
    }
    return (viewSize - snapSize) / 2 + MEDIA_GALLERY_PEEK_GAP_PX;
  };
};

type MediaGalleryCarouselAlign =
  | 'start'
  | 'center'
  | ReturnType<typeof createMediaGalleryPeekAlign>;

export interface MediaGalleryCarouselOptions {
  align: MediaGalleryCarouselAlign;
  containScroll: false | 'trimSnaps';
  loop?: false;
}

/**
 * Embla options for the width active at the current viewport. Pass the matching
 * tier from {@link resolveMediaGallerySlideTiers} so a phone-only peek never
 * keeps peek alignment after the layout has gone full-width or two-up.
 * Galleries without a configured width keep the defaults: the entry gallery
 * centers and loops, the one-up viewer pins to the start and stops at the ends.
 * Looping turns off for any non-full width so the first photo never shows the
 * last one beside it.
 */
export const resolveMediaGalleryCarouselOptions = ({
  showAsCarousel = false,
  slideCount,
  activeWidth,
  ...slideBasisProps
}: MediaGallerySlideBasisProps & {
  showAsCarousel?: boolean;
  slideCount: number;
  /** Width for the current viewport. Omit to use the phone (`slideBasis`) tier. */
  activeWidth?: CarouselItemBasisToken | CarouselItemBasisBreakpointToken;
}): MediaGalleryCarouselOptions => {
  const tiers = resolveMediaGallerySlideTiers(slideBasisProps);
  const width = activeWidth !== undefined ? activeWidth : tiers.base;
  const allDefault =
    isDefaultSlideWidth(tiers.base) &&
    isDefaultSlideWidth(tiers.md) &&
    isDefaultSlideWidth(tiers.lg);

  let align: MediaGalleryCarouselAlign;
  let containScroll: false | 'trimSnaps';
  if (width === 'peek') {
    align = createMediaGalleryPeekAlign(slideCount);
    containScroll = false;
  } else if (isDefaultSlideWidth(width)) {
    align = showAsCarousel ? 'start' : 'center';
    containScroll = 'trimSnaps';
  } else {
    align = 'center';
    containScroll = false;
  }

  if (allDefault) {
    return showAsCarousel
      ? { align, containScroll, loop: false }
      : { align, containScroll };
  }
  return { align, containScroll, loop: false };
};

/**
 * One-up slides lock to the frame width so a contained portrait photo cannot
 * shrink its slide and reveal neighbors. The lock is lifted only at breakpoints
 * with a configured non-full width.
 */
const resolveMediaGallerySlideLockClassName = (
  slideBasisProps: MediaGallerySlideBasisProps,
): string => {
  const tiers = resolveMediaGallerySlideTiers(slideBasisProps);
  const base = isDefaultSlideWidth(tiers.base);
  const md = isDefaultSlideWidth(tiers.md);
  const lg = isDefaultSlideWidth(tiers.lg);
  return cn(
    base && 'w-full min-w-full',
    md !== base && (md ? 'md:w-full md:min-w-full' : 'md:min-w-0'),
    lg !== md && (lg ? 'lg:w-full lg:min-w-full' : 'lg:min-w-0'),
  );
};

export interface MediaGalleryLibraryProps {
  groups: MediaGroup[];
  /**
   * Leave it out inside a popup that has its own title, such as the Media
   * Gallery Viewer's Modal, so the title is not shown twice.
   */
  title?: string;
  positionIndex?: number;
  onSelectedIndexChange?: (index: number, item: MediaItem) => void;
  loadingLabel?: string;
  errorLabel?: string;
  imageBorder?: boolean;
}

interface MediaTileProps {
  item: MediaItem;
  index: number;
  selected: boolean;
  onSelect: (index: number) => void;
  buttonRef?: (element: HTMLButtonElement | null) => void;
  loadingLabel: string;
  errorLabel: string;
  className?: string;
  imageBorder?: boolean;
  imageFit?: MediaImageFit;
  intrinsicImage?: boolean;
  reducedMotion?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
}

/**
 * Flattens library groups into the order `MediaGalleryLibrary` reports indices
 * against, so a consumer can hand the same array to the carousel view.
 */
export const flattenMediaGroups = (groups: MediaGroup[]): MediaItem[] =>
  groups.flatMap((group) => (Array.isArray(group?.items) ? group.items : []));

const clampIndex = (index: number | undefined, length: number): number => {
  if (length === 0) return 0;
  return Math.min(Math.max(index ?? 0, 0), length - 1);
};

/** Resolves once the popup around `node`, if any, has finished animating in. */
const whenPopupSettled = (node: HTMLElement | null | undefined) => {
  const popup = node?.closest<HTMLElement>('[data-slot="dialog-content"]');
  const running =
    typeof popup?.getAnimations === 'function' ? popup.getAnimations() : [];
  return Promise.all(
    running.map((animation) => animation.finished.catch(() => undefined)),
  );
};

/**
 * True when `node` is at least partly visible in both areas that can scroll
 * it: the library list and the popup around it.
 */
const isInScrollView = (node: HTMLElement): boolean => {
  const rect = node.getBoundingClientRect();
  return [
    node.closest<HTMLElement>('[data-testid="media-library-scroll"]'),
    node.closest<HTMLElement>('[data-slot="dialog-content"]'),
  ].every((scroller) => {
    if (!scroller) return true;
    const box = scroller.getBoundingClientRect();
    return rect.bottom > box.top && rect.top < box.bottom;
  });
};

const getLayoutName = (length: number): string => {
  if (length >= 5) return 'hero-four';
  if (length === 4) return 'hero-three';
  if (length === 3) return 'hero-two';
  if (length === 2) return 'two-up';
  return 'single';
};

/**
 * Library rows hold one hero plus two stacked photos and keep that shape even
 * when a collection's last row is short, so collections of any size line up.
 */
const LIBRARY_GROUP_LAYOUT = 'hero-two';

const getDesktopGridClass = (length: number): string => {
  if (length >= 3) return 'grid-cols-4 grid-rows-2';
  if (length === 2) return 'grid-cols-2 grid-rows-1';
  return 'grid-cols-1 grid-rows-1';
};

const getDesktopTileClass = (length: number, index: number): string => {
  if (length >= 3 && index === 0) return 'col-span-2 row-span-2';
  if (length === 3) return 'col-span-2';
  if (length === 4) return index === 3 ? 'col-span-2' : '';
  return '';
};

const MediaPlaceholder = ({
  label,
  status,
}: {
  label: string;
  status: Exclude<MediaItemStatus, 'ready'>;
}) => (
  <Flex
    direction='row'
    align='center'
    justify='center'
    gap='none'
    width='full'
    height='full'
    role={status === 'error' ? 'alert' : 'status'}
    className={cn(
      'min-h-32 rounded-md px-sm py-lg text-center',
      status === 'loading'
        ? 'animate-pulse bg-neutral-50 motion-reduce:animate-none'
        : 'bg-error-100',
    )}
  >
    <Text
      tag='span'
      variant='b4'
      color={status === 'error' ? 'error' : 'subtext'}
    >
      {label}
    </Text>
  </Flex>
);

const MediaTile = ({
  item,
  index,
  selected,
  onSelect,
  buttonRef,
  loadingLabel,
  errorLabel,
  className,
  imageBorder = true,
  imageFit = 'cover',
  intrinsicImage = false,
  reducedMotion = false,
  onKeyDown,
}: MediaTileProps) => {
  const [imageFailed, setImageFailed] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);
  const status = imageFailed ? 'error' : (item.status ?? 'ready');
  const releasePress = () => setPressed(false);

  return (
    <Flex
      gap='none'
      className={cn(
        'group relative min-h-0 min-w-0',
        !intrinsicImage && 'rounded-md',
        // Keep the slide's full frame width even when the photo is portrait.
        // An intrinsically sized <img> would otherwise shrink the Embla slide
        // and let the previous/next photos peek in as thin side lines.
        intrinsicImage && 'w-full min-w-full bg-neutral-900',
        className,
      )}
    >
      {status === 'loading' ? (
        <MediaPlaceholder label={loadingLabel} status='loading' />
      ) : status === 'error' ? (
        <MediaPlaceholder label={errorLabel} status='error' />
      ) : (
        <Image
          src={item.src}
          alt=''
          frame='bleed'
          // One-up always uses the 4:3 frame box. Photos are contained inside
          // it so portrait and landscape never change the slide width Embla
          // measures, and adjacent slides stay clipped.
          aspect='4/3'
          objectFit={intrinsicImage ? 'contain' : imageFit}
          rounded={intrinsicImage ? false : 'md'}
          border={imageBorder}
          className={cn(
            intrinsicImage
              ? 'w-full max-h-[min(80vh,calc(var(--carousel-photo-width,100vw)*3/4))] bg-neutral-900'
              : 'h-full w-full',
            // Press zoom is for the library / entry tiles only. The one-up
            // onDark carousel keeps the photo still so swipe and arrows own
            // the motion.
            !intrinsicImage &&
              'transition-transform duration-slower ease-emphasized motion-reduce:transition-none',
            '[&_img]:transition-[opacity,filter] [&_img]:duration-slow [&_img]:ease-emphasized',
            '[&_img]:group-hover:brightness-90 [&_img]:motion-reduce:transition-none',
            !intrinsicImage && pressed && 'scale-press-zoom-out',
          )}
          onError={() => setImageFailed(true)}
        />
      )}
      <Button
        ref={(element) =>
          buttonRef?.(element instanceof HTMLButtonElement ? element : null)
        }
        type='button'
        variant='textOnlyNeutral'
        size='flush'
        aria-label={item.alt}
        aria-pressed={selected}
        className={cn(
          'absolute inset-0 z-10 block h-full min-h-11 w-full min-w-11 overflow-hidden',
          !intrinsicImage && 'rounded-md',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-1 focus-visible:ring-offset-focus',
          'hover:bg-transparent hover:no-underline motion-reduce:transition-none',
        )}
        onClick={() => onSelect(index)}
        onPointerDown={() => {
          if (!intrinsicImage && !reducedMotion) setPressed(true);
        }}
        onPointerUp={releasePress}
        onPointerCancel={releasePress}
        onPointerLeave={releasePress}
        onBlur={releasePress}
        onKeyDown={(event) => {
          if (
            !intrinsicImage &&
            !reducedMotion &&
            (event.key === 'Enter' || event.key === ' ')
          ) {
            setPressed(true);
          }
          onKeyDown?.(event);
        }}
        onKeyUp={releasePress}
      />
    </Flex>
  );
};

/** Matches the photos' `max-h-[80vh]` soft cap. */
const CAROUSEL_PHOTO_MAX_HEIGHT = 0.8;
/** The gallery's regular 4:3 photo shape sets the one-up frame width. */
const CAROUSEL_FRAME_RATIO = 4 / 3;
/**
 * Fallback for both beside arrows plus their gaps (`h-11` + `gap-x-sm` × 2)
 * when the arrow columns are not measurable yet.
 */
const CAROUSEL_ARROW_ROOM_FALLBACK_PX = 120;
/** Fallback when the header hit area is not measurable yet (size-11). */
const CAROUSEL_HEADER_FALLBACK_PX = 44;

export const MediaGallery = ({
  items: itemsProp,
  groups,
  selectedIndex,
  defaultSelectedIndex = 0,
  onSelectedIndexChange,
  onItemActivate,
  lastVisibleOverlay,
  overlayActions,
  showAsCarousel = false,
  bleedSectionPadding = true,
  carouselHeader,
  slideBasis,
  'slideBasis-md': slideBasisMd,
  'slideBasis-lg': slideBasisLg,
  imageBorder = true,
  imageFit = 'cover',
  showCount = true,
  countPlacement = 'end',
  countIcon,
  label = 'Media gallery',
  loadingLabel = 'Loading media',
  errorLabel = 'Media unavailable',
}: MediaGalleryProps) => {
  const items = React.useMemo(() => {
    const grouped = Array.isArray(groups) ? flattenMediaGroups(groups) : [];
    if (grouped.length > 0) return grouped;
    return Array.isArray(itemsProp) ? itemsProp : [];
  }, [groups, itemsProp]);
  // One-up viewer: a phone-only peek returns to one full photo from tablet up
  // unless the editor sets an explicit tablet width.
  const slideBasisMdResolved =
    slideBasisMd ??
    (showAsCarousel && !isDefaultSlideWidth(slideBasis) ? 'full' : undefined);
  const slideBasisProps = React.useMemo(
    () => ({
      slideBasis,
      'slideBasis-md': slideBasisMdResolved,
      'slideBasis-lg': slideBasisLg,
    }),
    [slideBasis, slideBasisMdResolved, slideBasisLg],
  );
  const isMdUp = useMatchesMedia(MEDIA_GALLERY_MD_QUERY);
  const isLgUp = useMatchesMedia(MEDIA_GALLERY_LG_QUERY);
  const activeSlideWidth = React.useMemo(() => {
    const tiers = resolveMediaGallerySlideTiers(slideBasisProps);
    if (isLgUp) return tiers.lg;
    if (isMdUp) return tiers.md;
    return tiers.base;
  }, [slideBasisProps, isMdUp, isLgUp]);
  const carouselOptions = React.useMemo(
    () =>
      resolveMediaGalleryCarouselOptions({
        showAsCarousel,
        slideCount: items.length,
        activeWidth: activeSlideWidth,
        ...slideBasisProps,
      }),
    [showAsCarousel, items.length, activeSlideWidth, slideBasisProps],
  );
  const slideLockClassName =
    resolveMediaGallerySlideLockClassName(slideBasisProps);
  const isControlled = selectedIndex !== undefined;
  const [uncontrolledIndex, setUncontrolledIndex] = React.useState(() =>
    clampIndex(defaultSelectedIndex, items.length),
  );
  const currentIndex = clampIndex(
    isControlled ? selectedIndex : uncontrolledIndex,
    items.length,
  );
  // Keep Embla's mount index stable. Feeding the live index back as startIndex
  // makes embla-carousel-react reInit on every committed swipe and drops the
  // settle animation. Later index changes go through scrollTo below.
  const [initialIndex] = React.useState(currentIndex);
  const carouselOpts = React.useMemo(
    () => ({ startIndex: initialIndex, ...carouselOptions }),
    [initialIndex, carouselOptions],
  );
  const [carouselApi, setCarouselApi] = React.useState<CarouselApi>();
  const desktopButtonRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const carouselShellRef = React.useRef<HTMLElement | null>(null);
  const [carouselShellHost, setCarouselShellHost] =
    React.useState<HTMLElement | null>(null);
  const setCarouselShell = React.useCallback((node: HTMLElement | null) => {
    carouselShellRef.current = node;
    setCarouselShellHost(node);
  }, []);
  const reducedMotion = useReducedMotion(carouselShellHost);
  const selectIndex = React.useCallback(
    (nextIndex: number) => {
      if (items.length === 0) return;
      const next = clampIndex(nextIndex, items.length);
      if (!isControlled) setUncontrolledIndex(next);
      if (
        next !== currentIndex &&
        typeof onSelectedIndexChange === 'function'
      ) {
        onSelectedIndexChange(next, items[next]);
      }
    },
    [currentIndex, isControlled, items, onSelectedIndexChange],
  );

  /**
   * Choosing a tile reports activation even when the index does not move, so a
   * consumer can open a viewer on the already-selected image. Position sync
   * stays change-only, which keeps browsing separable from activation.
   */
  const activateIndex = React.useCallback(
    (nextIndex: number) => {
      if (items.length === 0) return;
      const next = clampIndex(nextIndex, items.length);
      if (!isControlled) setUncontrolledIndex(next);
      if (
        next !== currentIndex &&
        typeof onSelectedIndexChange === 'function'
      ) {
        onSelectedIndexChange(next, items[next]);
      }
      if (typeof onItemActivate === 'function') {
        onItemActivate(next, items[next]);
      }
    },
    [currentIndex, isControlled, items, onItemActivate, onSelectedIndexChange],
  );

  React.useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => selectIndex(carouselApi.selectedScrollSnap());
    carouselApi.on('select', onSelect);
    return () => {
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi, selectIndex]);

  React.useEffect(() => {
    if (!carouselApi || carouselApi.selectedScrollSnap() === currentIndex)
      return;
    carouselApi.scrollTo(currentIndex, reducedMotion);
  }, [carouselApi, currentIndex, reducedMotion]);

  // The one-up carousel uses one fixed photo frame for every slide, so Back,
  // the count, and the arrows hold still while browsing. Narrower photos
  // (portrait) center inside it; no photo grows wider than it. Bind to the
  // shell's window so an authoring-preview iframe is measured, not the editor.
  React.useLayoutEffect(() => {
    if (!showAsCarousel || !carouselHeader) return undefined;
    const shell = carouselShellRef.current;
    const stage = shell?.querySelector<HTMLElement>(
      '[data-slot="carousel-beside-stage"]',
    );
    const view = shell?.ownerDocument.defaultView;
    if (!shell || !stage || !view) return undefined;

    const wideQuery =
      typeof view.matchMedia === 'function'
        ? view.matchMedia('(min-width: 768px)')
        : null;

    const syncFrameWidth = () => {
      // From md, leave room for beside arrows; below md they are hidden.
      // Measure the arrow columns and column gap the same way as chrome height
      // so publisher spacing-token overrides are respected.
      let arrowRoom = 0;
      if (wideQuery?.matches) {
        const previous = stage.querySelector<HTMLElement>(
          '[data-slot="carousel-previous"]',
        );
        const next = stage.querySelector<HTMLElement>(
          '[data-slot="carousel-next"]',
        );
        const columnGap =
          Number.parseFloat(view.getComputedStyle(stage).columnGap || '') || 0;
        const previousWidth = previous?.getBoundingClientRect().width ?? 0;
        const nextWidth = next?.getBoundingClientRect().width ?? 0;
        const measured = previousWidth + nextWidth + columnGap * 2;
        arrowRoom = measured > 0 ? measured : CAROUSEL_ARROW_ROOM_FALLBACK_PX;
      }
      const availableWidth = shell.clientWidth - arrowRoom;
      // Shell can measure 0 before layout; keep the previous width.
      if (availableWidth <= 0) return;

      const headerButton = stage.querySelector('button');
      const headerHeight =
        headerButton?.getBoundingClientRect().height ||
        CAROUSEL_HEADER_FALLBACK_PX;
      const rowGap = wideQuery?.matches
        ? Number.parseFloat(view.getComputedStyle(stage).rowGap || '') || 0
        : 0;
      const photoWrap = shell.closest<HTMLElement>(
        '[data-testid="media-viewer-carousel-photo"]',
      );
      const wrapStyle = photoWrap
        ? view.getComputedStyle(photoWrap)
        : undefined;
      const topPad = wrapStyle
        ? Number.parseFloat(wrapStyle.paddingTop || '') || 0
        : 0;
      const bottomPad = wrapStyle
        ? Number.parseFloat(wrapStyle.paddingBottom || '') || 0
        : 0;
      const chromeHeight = headerHeight + rowGap + topPad + bottomPad;
      const maxPhotoHeight = Math.min(
        view.innerHeight * CAROUSEL_PHOTO_MAX_HEIGHT,
        Math.max(0, view.innerHeight - chromeHeight),
      );
      const frameWidth = maxPhotoHeight * CAROUSEL_FRAME_RATIO;
      // Prefer an even pixel width so flex centering lands on whole pixels and
      // Embla translates are less likely to show a hairline of the next slide.
      let pixels = Math.floor(Math.min(availableWidth, frameWidth));
      if (pixels % 2 !== 0) pixels -= 1;
      if (pixels <= 0) return;
      const width = `${pixels}px`;
      if (stage.style.getPropertyValue('--carousel-photo-width') !== width) {
        stage.style.setProperty('--carousel-photo-width', width);
      }
    };

    syncFrameWidth();
    const frame = view.requestAnimationFrame(syncFrameWidth);
    wideQuery?.addEventListener('change', syncFrameWidth);
    view.addEventListener('resize', syncFrameWidth);
    return () => {
      view.cancelAnimationFrame(frame);
      wideQuery?.removeEventListener('change', syncFrameWidth);
      view.removeEventListener('resize', syncFrameWidth);
      stage.style.removeProperty('--carousel-photo-width');
    };
  }, [showAsCarousel, carouselHeader]);

  const handleDesktopKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const visibleCount = Math.min(items.length, 5);
    let next = index;
    if (event.key === 'ArrowLeft') next = Math.max(0, index - 1);
    if (event.key === 'ArrowRight')
      next = Math.min(visibleCount - 1, index + 1);
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = visibleCount - 1;
    selectIndex(next);
    desktopButtonRefs.current[next]?.focus();
  };

  const visibleItems = items.slice(0, 5);
  const libraryCta = !lastVisibleOverlay ? null : showAsCarousel ? (
    lastVisibleOverlay
  ) : (
    <Flex gap='none' className='hidden min-[800px]:flex'>
      {lastVisibleOverlay}
    </Flex>
  );
  const mediaCount =
    showCount && items.length > 0 ? (
      <Button
        type='button'
        variant='grayOutline'
        size='xs'
        iconBefore={countIcon}
        aria-label={`Photo ${currentIndex + 1} of ${items.length}`}
        data-testid='media-gallery-count'
        className={cn(!showAsCarousel && 'min-[800px]:hidden')}
        onClick={() => activateIndex(currentIndex)}
      >
        <span aria-live='polite'>
          {currentIndex + 1} / {items.length}
        </span>
      </Button>
    ) : null;

  return (
    <Section
      aria-label={label}
      data-hen-id={COMPONENT_IDS.MediaGallery}
      variant='default'
      padding='none'
      gap='min'
      className={cn(
        'relative gap-0 motion-reduce:scroll-auto',
        // Bleed to the exact viewport edges below the 800px grid. A fixed
        // gutter token would overshoot pages whose side padding differs from it
        // (the VDP uses 12px), widening the photos and shrinking the peeks.
        bleedSectionPadding &&
          !showAsCarousel &&
          'max-[799px]:left-1/2 max-[799px]:w-screen max-[799px]:-translate-x-1/2',
        showAsCarousel && carouselHeader && 'h-full min-h-0',
      )}
    >
      <Grid
        columns={1}
        gap='lg'
        data-testid='media-gallery-desktop'
        data-layout={getLayoutName(items.length)}
        className={cn(
          'min-h-64 w-full gap-lg',
          // Entry gallery: swipe carousel below 800px; hero + four grid from 800px.
          showAsCarousel ? 'hidden' : 'hidden min-[800px]:grid',
          getDesktopGridClass(items.length),
        )}
      >
        {visibleItems.map((item, index) => (
          <MediaTile
            key={item.id}
            item={item}
            index={index}
            selected={currentIndex === index}
            onSelect={activateIndex}
            buttonRef={(element) => {
              desktopButtonRefs.current[index] = element;
            }}
            onKeyDown={(event) => handleDesktopKeyDown(event, index)}
            loadingLabel={loadingLabel}
            errorLabel={errorLabel}
            imageBorder={imageBorder}
            imageFit={imageFit}
            reducedMotion={reducedMotion}
            className={getDesktopTileClass(items.length, index)}
          />
        ))}
      </Grid>

      <Flex
        ref={setCarouselShell}
        gap='none'
        data-testid='media-gallery-carousel'
        className={cn(
          showAsCarousel && 'h-full min-h-0 w-full',
          !showAsCarousel && 'min-[800px]:hidden',
        )}
        onKeyDownCapture={(event) => {
          if (event.key === 'Home') {
            event.preventDefault();
            carouselApi?.scrollTo(0);
          } else if (event.key === 'End') {
            event.preventDefault();
            carouselApi?.scrollTo(items.length - 1);
          }
        }}
      >
        <Carousel
          autoPlay={false}
          showControls={showAsCarousel}
          showDots={false}
          arrowPosition='beside'
          arrowDesign='onDark'
          arrowSize='lg'
          setApi={setCarouselApi}
          besideStart={showAsCarousel ? carouselHeader : undefined}
          className={cn(showAsCarousel && carouselHeader && 'h-full gap-0')}
          viewportClassName={
            // Cancel the 14px inter-photo gutter on the track so the first
            // photo starts at its separate 16px Embla edge inset. Embla only
            // applies that inset (inline transform) after it mounts, so start
            // the track already shifted by it: first paint matches the settled
            // layout and nothing jumps. Embla's inline transform replaces this.
            activeSlideWidth === 'peek'
              ? cn(
                  MEDIA_GALLERY_PEEK_TRACK_RELATIVE_CLASS,
                  initialIndex === 0 && MEDIA_GALLERY_PEEK_EDGE_SHIFT_CLASS,
                )
              : undefined
          }
          opts={carouselOpts}
          aria-label={`${label} carousel`}
        >
          {items.map((item, index) => (
            <CarouselItem
              key={item.id}
              basis={slideBasis}
              basis-md={slideBasisMdResolved}
              basis-lg={slideBasisLg}
              className={cn(
                // Keep 14px only between photos; Embla owns the 16px end insets.
                activeSlideWidth === 'peek' &&
                  MEDIA_GALLERY_PEEK_SLIDE_GUTTER_CLASS,
                // MediaGallery uses a tighter listing-gallery peek than the
                // generic Carousel's 88%. At 90%, the 14px slide gap leaves a
                // thin neighbor (~cars.com VDP) instead of a wide side strip.
                slideBasis === 'peek' && 'basis-[90%]',
                slideBasisMdResolved === 'peek' && 'md:basis-[90%]',
                slideBasisLg === 'peek' && 'lg:basis-[90%]',
                // From md the photo hangs from the top of the slide so the
                // header stays a spacing-token gap above it. `pl-0` pairs with
                // the besideStart track's `ml-0`; headerless carousels keep the
                // default `-ml-4` / `pl-4` gutter so the photo is not clipped.
                showAsCarousel &&
                  cn(
                    'flex items-center justify-center overflow-hidden md:items-start',
                    slideLockClassName,
                    carouselHeader && 'pl-0',
                  ),
                // Default entry gallery: two-up from tablet until the grid at
                // 800px. A phone peek/custom width keeps that width until the
                // grid unless slideBasis-md overrides tablet.
                !showAsCarousel &&
                  slideBasisMd === undefined &&
                  isDefaultSlideWidth(slideBasis) &&
                  'md:basis-1/2',
              )}
            >
              <MediaTile
                item={item}
                index={index}
                selected={currentIndex === index}
                onSelect={activateIndex}
                loadingLabel={loadingLabel}
                errorLabel={errorLabel}
                imageBorder={imageBorder}
                imageFit={imageFit}
                intrinsicImage={showAsCarousel}
                reducedMotion={reducedMotion}
              />
            </CarouselItem>
          ))}
        </Carousel>
      </Flex>

      {overlayActions || libraryCta || mediaCount ? (
        <Flex
          direction='row'
          align='center'
          // Peek widens the gap; other widths keep the original xs token.
          gap={activeSlideWidth === 'peek' ? 'sm' : 'xs'}
          data-testid='media-gallery-actions'
          className={cn(
            'pointer-events-auto absolute z-30',
            activeSlideWidth === 'peek'
              ? cn(
                  MEDIA_GALLERY_PEEK_OVERLAY_BOTTOM_CLASS,
                  MEDIA_GALLERY_PEEK_OVERLAY_LEFT_CLASS,
                )
              : 'bottom-xs left-xs',
            !showAsCarousel && 'min-[800px]:right-xs min-[800px]:left-auto',
          )}
        >
          {countPlacement === 'start' ? mediaCount : null}
          {overlayActions}
          {libraryCta}
          {countPlacement === 'end' ? mediaCount : null}
        </Flex>
      ) : null}
    </Section>
  );
};

MediaGallery.displayName = 'MediaGallery';

export const MediaGalleryLibrary = ({
  groups,
  title,
  positionIndex,
  onSelectedIndexChange,
  loadingLabel = 'Loading media',
  errorLabel = 'Media unavailable',
  imageBorder = true,
}: MediaGalleryLibraryProps) => {
  const hasTitle = Boolean(title?.trim());
  const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  const appliedPositionRef = React.useRef<number | null>(null);
  // When the Modal around it draws its close, the title band lines up with
  // it: same top inset, height, and right inset.
  const inPopup = useEnclosingModal()?.showsCloseButton === true;
  const [libraryHost, setLibraryHost] = React.useState<HTMLElement | null>(
    null,
  );
  const reducedMotion = useReducedMotion(libraryHost);
  // A direct caller can omit groups, or a group can omit items. Keep every
  // read on an array so the empty library renders instead of throwing.
  const safeGroups = React.useMemo(
    () =>
      (Array.isArray(groups) ? groups : []).map((group) =>
        Array.isArray(group?.items) ? group : { ...group, items: [] },
      ),
    [groups],
  );
  const flattenedItems = React.useMemo(
    () => flattenMediaGroups(safeGroups),
    [safeGroups],
  );
  const [selectedIndex, setSelectedIndex] = React.useState<number | undefined>(
    () =>
      positionIndex === undefined
        ? undefined
        : clampIndex(positionIndex, flattenedItems.length),
  );

  React.useEffect(() => {
    if (positionIndex === undefined) return;
    setSelectedIndex(clampIndex(positionIndex, flattenedItems.length));
  }, [flattenedItems.length, positionIndex]);

  /**
   * Keyed off the resolved index rather than `flattenedItems` so a consumer
   * that rebuilds `groups` inline does not yank focus back on every re-render.
   */
  React.useEffect(() => {
    if (positionIndex === undefined || flattenedItems.length === 0) return;
    const index = clampIndex(positionIndex, flattenedItems.length);
    if (appliedPositionRef.current === index) return;
    // Opening jumps straight to the photo; later moves glide. A smooth scroll
    // started while a popup animates in or moves focus gets cut short.
    const isFirstPosition = appliedPositionRef.current === null;
    let cancelled = false;
    const place = () => {
      if (cancelled || appliedPositionRef.current === index) return;
      const target = itemRefs.current[index];
      if (!target) return;
      appliedPositionRef.current = index;
      target.scrollIntoView({
        behavior: isFirstPosition || reducedMotion ? 'auto' : 'smooth',
        block: 'center',
      });
      // The popup focuses its close first. Move focus to the chosen photo
      // after that, without scrolling a second time.
      target.focus({ preventScroll: true });
    };
    // Fallback for a popup that moved the scroll while it opened.
    const restoreIfMoved = () => {
      if (cancelled || appliedPositionRef.current !== index) return;
      const target = itemRefs.current[index];
      if (!target || isInScrollView(target)) return;
      target.scrollIntoView({ behavior: 'auto', block: 'center' });
    };
    let scheduled = false;
    const placeAfterOpen = () => {
      if (scheduled) return;
      scheduled = true;
      // The dialog moves its own open focus right after this event. Placing
      // in a microtask lands after that focus and while the popup is still
      // fading in, so it opens on the photo instead of jumping to it.
      queueMicrotask(place);
      void whenPopupSettled(itemRefs.current[index]).then(restoreIfMoved);
    };

    const popup = itemRefs.current[index]?.closest<HTMLElement>(
      '[data-slot="dialog-content"]',
    );
    // No popup: nothing else will move focus, so place now.
    if (!popup) {
      place();
      return;
    }

    // The dialog dispatches this after its own open focus. Waiting for it,
    // then for the open animation, keeps that focus and animation from
    // undoing the scroll.
    popup.addEventListener(DIALOG_OPENED_EVENT, placeAfterOpen);
    // Photos that arrive after open, and Back remounting the library, miss
    // the event. The dialog is already open, so place once effects finish.
    queueMicrotask(() => {
      if (!cancelled && popup.getAttribute('data-state') === 'open') {
        placeAfterOpen();
      }
    });
    return () => {
      cancelled = true;
      popup.removeEventListener(DIALOG_OPENED_EVENT, placeAfterOpen);
    };
  }, [flattenedItems.length, positionIndex, reducedMotion]);

  let itemIndex = 0;

  return (
    <Section
      ref={setLibraryHost}
      data-testid='media-gallery-library'
      data-hen-id={COMPONENT_IDS.MediaGalleryLibrary}
      aria-label={hasTitle ? title : undefined}
      variant='default'
      padding='none'
      gap='min'
      className='relative max-h-full min-h-0 gap-0'
    >
      {/* Closing belongs to the popup around the library. Untitled, there is
          nothing left to draw here. */}
      {hasTitle ? (
        <Section
          as='div'
          data-testid='media-library-header'
          variant='default'
          padding='none'
          gap='min'
          data-chrome={inPopup ? 'popup' : 'standalone'}
          className={cn(
            'sticky top-0 z-20 bg-white',
            inPopup ? 'py-md pl-lg pr-[4.5rem]' : 'px-sm py-xs',
          )}
        >
          <Flex
            direction='row'
            align='center'
            gap='sm'
            className={cn(inPopup && 'min-h-11')}
          >
            <Heading variant='h3'>{title}</Heading>
          </Flex>
        </Section>
      ) : null}

      {/* No overscroll containment: in a popup with no height limit this list
          has nothing to scroll, and containing the wheel would stop the popup
          scrolling while the pointer is over the photos. */}
      <Flex
        gap='none'
        data-testid='media-library-scroll'
        className={cn(
          'min-h-0 flex-1 overflow-y-auto motion-reduce:scroll-auto',
          // Untitled, the library sits in the popup body, and the popup's
          // Inner spacing lines it up with the popup title.
          inPopup ? (hasTitle ? 'px-lg pb-lg' : 'pb-lg') : 'p-sm',
        )}
      >
        {safeGroups.map((group) => {
          const groupStartIndex = itemIndex;
          itemIndex += group.items.length;

          return (
            <Section
              key={group.id}
              as='div'
              variant='default'
              padding='none'
              gap='min'
              className='gap-0'
            >
              {Array.from({
                length: Math.ceil(group.items.length / 3),
              }).map((_, tripletIndex) => {
                const triplet = group.items.slice(
                  tripletIndex * 3,
                  tripletIndex * 3 + 3,
                );
                return (
                  <Grid
                    columns={2}
                    gap='lg'
                    key={`${group.id}-${tripletIndex}`}
                    data-testid='media-library-group'
                    data-layout={LIBRARY_GROUP_LAYOUT}
                    className='mb-lg min-h-64 grid-rows-3'
                  >
                    {triplet.map((item, tripletItemIndex) => {
                      const absoluteIndex =
                        groupStartIndex + tripletIndex * 3 + tripletItemIndex;
                      return (
                        <MediaTile
                          key={item.id}
                          item={item}
                          index={absoluteIndex}
                          selected={selectedIndex === absoluteIndex}
                          onSelect={(index) => {
                            setSelectedIndex(index);
                            onSelectedIndexChange?.(
                              index,
                              flattenedItems[index],
                            );
                          }}
                          buttonRef={(element) => {
                            itemRefs.current[absoluteIndex] = element;
                          }}
                          loadingLabel={loadingLabel}
                          errorLabel={errorLabel}
                          imageBorder={imageBorder}
                          reducedMotion={reducedMotion}
                          className={cn(
                            tripletItemIndex === 0 && 'col-span-2 row-span-2',
                          )}
                        />
                      );
                    })}
                  </Grid>
                );
              })}
            </Section>
          );
        })}
      </Flex>
    </Section>
  );
};

MediaGalleryLibrary.displayName = 'MediaGalleryLibrary';
